import { useState, useEffect } from "react";
import { makeStyles, Typography, Box, Grid, CircularProgress } from "@material-ui/core";
import { useSelector } from "react-redux";

import { COLORS } from "../../../../../theme/colors";
import { Toggle } from "../../../../../components/toggle";
import { ShaDowBox } from "../../../../../components/boxes";
import { DoubleBarChart } from "../../../../../components/charts";
import { powerConsumptionCostsChartBars } from "../../../constants";
import { onlyZeroValuesInArray, getData } from "../../../../../services";
import { GraphFooter, NoDataCard } from "../../../../../components/parts";

const useStyles = makeStyles(() => ({
  consumptionBlock: {
    position: "absolute",
    top: "45%",
    left: "-18px",
    transform: "translateY(-50%)",
  },
  consumption: {
    color: COLORS.darks[0][50],
    fontSize: 10,
    transform: "rotate(-90deg)",
  },
  lineBlock: {
    position: "relative",
  },
  line: {
    position: "absolute",
    top: "-10px",
    left: 0,
    height: 210,
    borderRight: "1px dashed rgba(0, 0, 0, 0.25)",
  },
}));

const GRAPH_UNITS = ["M", "Y"];

export const PowerConsumptionCosts = ({ buildingId, complexId, titleFirst, items }) => {
  const classes = useStyles();
  const [byFilter, setByFilter] = useState(GRAPH_UNITS[1]);
  const [loading, setLoading] = useState(false);
  const title = titleFirst + ` - ${byFilter === GRAPH_UNITS[0] ? "Monthly" : "Yearly"}`;
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [chartData, setChartData] = useState([]);

  let url = `/praas/v1/buildings/consumption-cost/?avg-format=${byFilter.toLowerCase()}`;
  let footerNames = ["Avg. portfolio", "Avg. Industry"];

  if (buildingId) {
    url = `/praas/v2/buildings/${buildingId}/properties/consumption-cost/?avg-format=${byFilter.toLowerCase()}`;
    footerNames = ["In this building", "Avg. portfolio", "Avg. Industry"];
  } else if (complexId) {
    url = `/praas/v2/complexes/${complexId}/properties/consumption-cost/?avg-format=${byFilter.toLowerCase()}`;
    footerNames = ["In this complex", "Avg. portfolio", "Avg. Industry"];
    items = [
      {
        name: "In this complex",
        color: COLORS.blue,
      },
      {
        name: "Avg. portfolio",
        color: COLORS.spanishGray,
      },
      {
        name: "Avg. Industry",
        color: COLORS.darks[0][100],
      },
    ];
  }

  useEffect(() => {
    setLoading(true);
    getData(url, (data) => {
      if (
        !onlyZeroValuesInArray([data.public.building, data.rental.building, data.total.building])
      ) {
        setChartData([
          { name: "Public area", unit: "perSquare", ...data.public },
          { name: "Rental units", unit: "perSquare", ...data.rental },
          { name: "Total Building", unit: "perSquare", ...data.total },
        ]);
      }
    }).finally(() => setLoading(false));

    return () => setChartData([]);
  }, [buildingId, complexId, byFilter]);

  if (!chartData.length && !loading) {
    return <NoDataCard title={titleFirst} footerNames={footerNames} />;
  }

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h4">{title}</Typography>
          </Grid>
          <Grid item container alignItems="center" style={{ width: "auto" }}>
            <Typography style={{ marginRight: 4 }} variant="body2">
              By:
            </Typography>
            <Toggle
              value={byFilter}
              options={GRAPH_UNITS}
              onChanged={setByFilter}
              styles={{ width: 30, fontSize: 12 }}
            />
          </Grid>
        </Grid>
        <Box style={{ marginTop: 40, position: "relative" }}>
          {loading ? (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 235 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid container style={{ paddingLeft: 60 }}>
                <Grid item xs={4}></Grid>
                <Grid item xs={4} className={classes.lineBlock}>
                  <Box className={classes.line}></Box>
                </Grid>
                <Grid item xs={4} className={classes.lineBlock}>
                  <Box className={classes.line}></Box>
                </Grid>
              </Grid>
              <Box className={classes.consumptionBlock}>
                <Typography className={classes.consumption}>
                  {currencySymbol}/{user?.unit_system}
                </Typography>
              </Box>
              <Box style={{ height: 235 }}>
                <DoubleBarChart
                  data={chartData}
                  bars={powerConsumptionCostsChartBars}
                  vertical={false}
                  horizontal={true}
                  xAxisTickLine={{ stroke: COLORS.darks[0][50] }}
                  xAxisStroke={COLORS.darks[0][100]}
                  hideXAxis={false}
                  hideSum={true}
                />
              </Box>
            </>
          )}
        </Box>
        <GraphFooter items={items} />
      </Box>
    </ShaDowBox>
  );
};
